import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  DollarSign,
  TrendingUp,
  CreditCard,
  Shield,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  ArrowLeft,
  Upload,
  FileText,
  RefreshCw,
  BookOpen,
  Link,
  Globe,
  Loader2,
  UserPlus,
  Gift,
  Eye,
  ExternalLink,
  Database,
  Plus,
  Edit,
  Scale,
  AlertTriangle,
  Info,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL, API_URL } from '../config';

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ status: 'active', days_to_add: 30 });
  const [activeTab, setActiveTab] = useState('users'); // users, knowledge, transactions, free-accounts, portals
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [showFreeAccountDialog, setShowFreeAccountDialog] = useState(false);
  const [freeAccountForm, setFreeAccountForm] = useState({ email: '', full_name: '', password: '' });
  const [creatingFreeAccount, setCreatingFreeAccount] = useState(false);
  // Portal credentials state
  const [portalCredentials, setPortalCredentials] = useState([]);
  const [portalForm, setPortalForm] = useState({ username: '', password: '' });
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [savingPortal, setSavingPortal] = useState(false);
  const [testingPortal, setTestingPortal] = useState(null);
  // Document preview state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  // Critical tax data state
  const [criticalTaxData, setCriticalTaxData] = useState([]);
  const [showTaxDataDialog, setShowTaxDataDialog] = useState(false);
  const [editingTaxData, setEditingTaxData] = useState(null);
  const [taxDataForm, setTaxDataForm] = useState({
    name: '', value: '', category: 'VAT', year: new Date().getFullYear(), legal_basis: '', notes: ''
  });
  // Law changes state
  const [lawChanges, setLawChanges] = useState([]);
  // Quality (Golden Set / Audit learning) state
  const [goldenCases, setGoldenCases] = useState([]);
  const [goldenRun, setGoldenRun] = useState(null);
  const [runningGolden, setRunningGolden] = useState(false);
  const [qualityTrend, setQualityTrend] = useState([]);
  const [kbGaps, setKbGaps] = useState([]);
  const [lowQualityQueries, setLowQualityQueries] = useState([]);
  const [abStats, setAbStats] = useState({ A: {}, B: {} });
  const [searchStats, setSearchStats] = useState(null);
  const [newGoldenCase, setNewGoldenCase] = useState({ question: '', expected_facts: '', topic: '' });
  const [showLawChangeDialog, setShowLawChangeDialog] = useState(false);
  const [editingLawChange, setEditingLawChange] = useState(null);
  const [lawChangeForm, setLawChangeForm] = useState({
    title: '', 
    description: '', 
    severity: 'info', 
    effective_date: '', 
    legal_basis: '', 
    category: '',
    who_affected: '',      // Kogo dotyczy
    action_required: '',   // Co musisz zrobić
    consequences: '',      // Konsekwencje nieprzestrzegania
    deadline: ''           // Termin wdrożenia
  });
  const [emailForm, setEmailForm] = useState({ subject: '', message: '', target: 'all' });
  const [sendingEmail, setSendingEmail] = useState(false);
  // Daily questions state
  const [dailyQuestions, setDailyQuestions] = useState([]);
  const [showDailyQuestionDialog, setShowDailyQuestionDialog] = useState(false);
  const [dailyQuestionForm, setDailyQuestionForm] = useState({ question: '', category: 'VAT', hint: '', date: '' });
  // Verification stats state
  const [verificationStats, setVerificationStats] = useState(null);
  // Knowledge sources state
  const [knowledgeSources, setKnowledgeSources] = useState(null);
  const [refreshingSource, setRefreshingSource] = useState(null);
  // AI Monitoring state
  const [aiMonitoringDashboard, setAiMonitoringDashboard] = useState(null);
  const [monitoringConversations, setMonitoringConversations] = useState([]);
  const [monitoringPage, setMonitoringPage] = useState(1);
  const [monitoringTotal, setMonitoringTotal] = useState(0);
  const [monitoringPages, setMonitoringPages] = useState(1);
  const [monitoringSearch, setMonitoringSearch] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showConversationDialog, setShowConversationDialog] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Dostęp tylko dla administratorów');
      navigate('/dashboard');
      return;
    }
    loadAdminData();
  }, [user]);

  const loadAdminData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, usersRes, transRes, knowledgeRes, portalsRes, taxDataRes, lawChangesRes, dailyQRes, verifRes, sourcesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`, { headers }),
        axios.get(`${API_URL}/admin/users`, { headers }),
        axios.get(`${API_URL}/admin/transactions`, { headers }),
        axios.get(`${API_URL}/admin/knowledge`, { headers }),
        axios.get(`${API_URL}/admin/portal-credentials`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/critical-tax-data`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/law-changes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/daily-questions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/verification-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/admin/knowledge-sources`, { headers }).catch(() => ({ data: null })),
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTransactions(transRes.data);
      setKnowledgeBase(knowledgeRes.data);
      setPortalCredentials(portalsRes.data);
      setCriticalTaxData(taxDataRes.data);
      setLawChanges(lawChangesRes.data);
      setDailyQuestions(dailyQRes.data);
      setVerificationStats(verifRes.data);
      setKnowledgeSources(sourcesRes.data);
      // Load AI monitoring data
      loadMonitoringData();
    } catch (error) {
      toast.error('Błąd ładowania danych administratora');
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonitoringData = async (page = 1, search = '') => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, convRes] = await Promise.all([
        axios.get(`${API_URL}/admin/ai-monitoring/dashboard`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/admin/ai-monitoring/conversations?page=${page}&search=${search}`, { headers }).catch(() => ({ data: { conversations: [], total: 0, pages: 1 } })),
      ]);
      setAiMonitoringDashboard(dashRes.data);
      setMonitoringConversations(convRes.data?.conversations || []);
      setMonitoringTotal(convRes.data?.total || 0);
      setMonitoringPages(convRes.data?.pages || 1);
    } catch (e) {
      console.error('Monitoring load error:', e);
    }
  };

  const loadConversationDetail = async (convId) => {
    try {
      setLoadingConversation(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/admin/ai-monitoring/conversation/${convId}`, { headers });
      setSelectedConversation(res.data);
      setConversationMessages(res.data?.messages || []);
      setShowConversationDialog(true);
    } catch (e) {
      toast.error('Błąd ładowania rozmowy');
    } finally {
      setLoadingConversation(false);
    }
  };

  const loadQualityData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [casesRes, trendRes, gapsRes, lowRes, abRes, searchRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/admin/quality/golden-cases`, { headers }).catch(() => ({ data: { cases: [] } })),
        axios.get(`${API_URL}/admin/quality/quality-trend?days=30`, { headers }).catch(() => ({ data: { data_points: [] } })),
        axios.get(`${API_URL}/admin/quality/kb-gaps?days=30`, { headers }).catch(() => ({ data: { gaps: [] } })),
        axios.get(`${API_URL}/admin/quality/low-quality-queries?days=30`, { headers }).catch(() => ({ data: { queries: [] } })),
        axios.get(`${API_URL}/admin/quality/ab-stats`, { headers }).catch(() => ({ data: { A: {}, B: {} } })),
        axios.get(`${API_URL}/admin/quality/search-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/admin/quality/golden-cases/history?limit=5`, { headers }).catch(() => ({ data: { runs: [] } })),
      ]);
      setGoldenCases(casesRes.data?.cases || []);
      setQualityTrend(trendRes.data?.data_points || []);
      setKbGaps(gapsRes.data?.gaps || []);
      setLowQualityQueries(lowRes.data?.queries || []);
      setAbStats(abRes.data || { A: {}, B: {} });
      setSearchStats(searchRes.data);
      setGoldenRun(historyRes.data?.runs?.[0] || null);
    } catch (e) {
      console.error('Quality load error:', e);
    }
  };

  const seedGoldenCases = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/admin/quality/golden-cases/seed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Zasiano domyślne przypadki testowe');
      loadQualityData();
    } catch {
      toast.error('Błąd zasiewania');
    }
  };

  const runGoldenSet = async () => {
    try {
      setRunningGolden(true);
      toast.info('Uruchamiam testy jakości — to może zająć 1-2 minuty...');
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/admin/quality/golden-cases/run`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      });
      setGoldenRun(res.data);
      toast.success(`Testy zakończone: ${res.data?.passed}/${res.data?.total_cases} zaliczone (średnia ${res.data?.avg_score}/100)`);
    } catch (e) {
      toast.error('Błąd uruchomienia testów: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRunningGolden(false);
    }
  };

  const createGoldenCase = async () => {
    if (!newGoldenCase.question || !newGoldenCase.expected_facts) {
      toast.error('Pytanie i fakty są wymagane');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const facts = newGoldenCase.expected_facts.split('\n').map(f => f.trim()).filter(Boolean);
      await axios.post(`${API_URL}/admin/quality/golden-cases`,
        { question: newGoldenCase.question, expected_facts: facts, topic: newGoldenCase.topic },
        { headers: { Authorization: `Bearer ${token}` } });
      setNewGoldenCase({ question: '', expected_facts: '', topic: '' });
      toast.success('Dodano przypadek testowy');
      loadQualityData();
    } catch {
      toast.error('Błąd zapisu');
    }
  };

  const deleteGoldenCase = async (caseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/quality/golden-cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usunięto');
      loadQualityData();
    } catch {
      toast.error('Błąd usuwania');
    }
  };



  const handleEditSubscription = (user) => {
    setSelectedUser(user);
    setEditForm({
      status: user.subscription?.status || 'active',
      days_to_add: 30,
    });
    setShowEditDialog(true);
  };

  const handleSaveSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/admin/users/${selectedUser.id}/subscription`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subskrypcja zaktualizowana');
      setShowEditDialog(false);
      loadAdminData();
    } catch (error) {
      toast.error('Błąd aktualizacji subskrypcji');
      console.error('Error updating subscription:', error);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć użytkownika ${userEmail}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Użytkownik usunięty');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd usuwania użytkownika');
      console.error('Error deleting user:', error);
    }
  };

  const handlePromoteToAdmin = async (userId, userEmail) => {
    if (!window.confirm(`Czy na pewno chcesz nadać uprawnienia administratora użytkownikowi ${userEmail}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/admin/users/${userId}/role`, 
        { role: 'admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Użytkownik awansowany na administratora');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd awansowania użytkownika');
      console.error('Error promoting user:', error);
    }
  };

  const handleSyncModels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/admin/models/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Modele zsynchronizowane: ${response.data.total_models} dostępnych`);
    } catch (error) {
      toast.error('Błąd synchronizacji modeli');
      console.error('Error syncing models:', error);
    }
  };

  const handleUploadKnowledge = async () => {
    if (!uploadFile || !uploadTitle) {
      toast.error('Wybierz plik i podaj tytuł');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      formData.append('description', 'Dokument bazy wiedzy');

      await axios.post(`${API_URL}/admin/knowledge/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success('Dokument przesłany do bazy wiedzy');
      setUploadFile(null);
      setUploadTitle('');
      // Reset file input
      const fileInput = document.getElementById('knowledge-file');
      if (fileInput) fileInput.value = '';
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd przesyłania dokumentu');
      console.error('Error uploading knowledge:', error);
    }
  };

  const handleAddUrlKnowledge = async () => {
    if (!urlInput) {
      toast.error('Podaj adres URL strony');
      return;
    }

    setIsLoadingUrl(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/admin/knowledge/url`, {
        url: urlInput,
        title: urlTitle || null,
        description: `Pobrano ze strony: ${urlInput}`
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(`Strona dodana do bazy wiedzy (${(response.data.content_length / 1024).toFixed(1)} KB)`);
      setUrlInput('');
      setUrlTitle('');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd pobierania strony');
      console.error('Error adding URL knowledge:', error);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const handleCreateFreeAccount = async () => {
    if (!freeAccountForm.email || !freeAccountForm.full_name || !freeAccountForm.password) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    setCreatingFreeAccount(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/admin/users/create-free-account`, freeAccountForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Darmowe konto utworzone dla: ${freeAccountForm.email}`);
      setShowFreeAccountDialog(false);
      setFreeAccountForm({ email: '', full_name: '', password: '' });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia konta');
      console.error('Error creating free account:', error);
    } finally {
      setCreatingFreeAccount(false);
    }
  };

  const handleDeleteKnowledge = async (knowledgeId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten dokument?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/knowledge/${knowledgeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Dokument usunięty');
      loadAdminData();
    } catch (error) {
      toast.error('Błąd usuwania dokumentu');
      console.error('Error deleting knowledge:', error);
    }
  };

  const handlePreviewDocument = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/knowledge/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewDocument(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      // If endpoint doesn't return full content, use what we have
      setPreviewDocument(doc);
      setShowPreviewDialog(true);
    }
  };

  const handleRefreshSource = async (apiEndpoint, sourceName) => {
    setRefreshingSource(sourceName);
    try {
      const token = localStorage.getItem('token');
      toast.info(`Odswiezanie: ${sourceName}...`);
      await axios.post(`${API_URL}${apiEndpoint.replace('/api', '')}`, {}, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 300000,
      });
      toast.success(`${sourceName} - odswiezanie uruchomione`);
      setTimeout(() => loadAdminData(), 3000);
    } catch {
      toast.error(`Blad odswiezania: ${sourceName}`);
    } finally {
      setRefreshingSource(null);
    }
  };

  // Portal credentials functions
  const handleSavePortalCredentials = async (portalName) => {
    if (!portalForm.username || !portalForm.password) {
      toast.error('Podaj login i hasło');
      return;
    }

    setSavingPortal(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/portal-credentials/${portalName}`,
        {
          portal_name: portalName,
          username: portalForm.username,
          password: portalForm.password,
          enabled: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dane logowania zapisane');
      setPortalForm({ username: '', password: '' });
      setSelectedPortal(null);
      loadAdminData();
    } catch (error) {
      toast.error('Błąd zapisywania danych logowania');
      console.error('Error saving portal credentials:', error);
    } finally {
      setSavingPortal(false);
    }
  };

  const handleTestPortal = async (portalName) => {
    setTestingPortal(portalName);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/portal-credentials/${portalName}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Błąd testowania połączenia');
    } finally {
      setTestingPortal(null);
    }
  };

  const handleDeletePortalCredentials = async (portalName) => {
    if (!window.confirm('Czy na pewno usunąć dane logowania do tego portalu?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/portal-credentials/${portalName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dane logowania usunięte');
      loadAdminData();
    } catch (error) {
      toast.error('Błąd usuwania danych logowania');
    }
  };

  // Critical Tax Data handlers
  const handleSaveTaxData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      if (editingTaxData) {
        await axios.put(`${API_URL}/admin/critical-tax-data/${editingTaxData.id}`, taxDataForm, { headers });
        toast.success('Dane zaktualizowane');
      } else {
        await axios.post(`${API_URL}/admin/critical-tax-data`, taxDataForm, { headers });
        toast.success('Dane dodane');
      }
      
      setShowTaxDataDialog(false);
      setEditingTaxData(null);
      setTaxDataForm({ name: '', value: '', category: 'VAT', year: new Date().getFullYear(), legal_basis: '', notes: '' });
      loadAdminData();
    } catch (error) {
      toast.error('Błąd zapisywania danych');
    }
  };

  const handleEditTaxData = (entry) => {
    setEditingTaxData(entry);
    setTaxDataForm({
      name: entry.name || '',
      value: entry.value || '',
      category: entry.category || 'VAT',
      year: entry.year || new Date().getFullYear(),
      legal_basis: entry.legal_basis || '',
      notes: entry.notes || ''
    });
    setShowTaxDataDialog(true);
  };

  const handleDeleteTaxData = async (entryId) => {
    if (!window.confirm('Czy na pewno usunąć tę pozycję?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/critical-tax-data/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pozycja usunięta');
      loadAdminData();
    } catch (error) {
      toast.error('Błąd usuwania pozycji');
    }
  };

  const handleToggleTaxDataActive = async (entry) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/critical-tax-data/${entry.id}`,
        { active: !entry.active },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success(entry.active ? 'Dezaktywowano' : 'Aktywowano');
      loadAdminData();
    } catch (error) {
      toast.error('Błąd zmiany statusu');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-surface">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="w-8 h-8 text-primary-900" />
            <h1 className="font-heading text-2xl text-primary-900">Panel Administratora</h1>
          </div>
          <Button onClick={() => navigate('/dashboard')} variant="ghost">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Wróć do Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-card p-6" data-testid="stat-total-users">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-primary-500" />
              <span className="text-sm text-muted-foreground">Wszyscy</span>
            </div>
            <p className="text-3xl font-bold text-primary-900">{stats?.total_users || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Użytkowników</p>
          </div>

          <div className="bg-white rounded-lg shadow-card p-6" data-testid="stat-active-subs">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <span className="text-sm text-muted-foreground">Aktywne</span>
            </div>
            <p className="text-3xl font-bold text-primary-900">{stats?.active_subscriptions || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Subskrypcji</p>
          </div>

          <div className="bg-white rounded-lg shadow-card p-6" data-testid="stat-monthly-revenue">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-secondary-500" />
              <span className="text-sm text-muted-foreground">30 dni</span>
            </div>
            <p className="text-3xl font-bold text-primary-900">{stats?.monthly_revenue?.toFixed(2) || 0} zł</p>
            <p className="text-sm text-muted-foreground mt-1">Przychód</p>
          </div>

          <div className="bg-white rounded-lg shadow-card p-6" data-testid="stat-total-revenue">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-green-600" />
              <span className="text-sm text-muted-foreground">Łącznie</span>
            </div>
            <p className="text-3xl font-bold text-primary-900">{stats?.total_revenue?.toFixed(2) || 0} zł</p>
            <p className="text-sm text-muted-foreground mt-1">Całkowity przychód</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-card p-2 mb-8 flex gap-2 flex-wrap">
          <Button
            onClick={() => setActiveTab('users')}
            variant={activeTab === 'users' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Users className="w-4 h-4 mr-2" />
            Użytkownicy
          </Button>
          <Button
            onClick={() => setActiveTab('knowledge')}
            variant={activeTab === 'knowledge' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Baza Wiedzy
          </Button>
          <Button
            onClick={() => setActiveTab('portals')}
            variant={activeTab === 'portals' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Globe className="w-4 h-4 mr-2" />
            Portale
          </Button>
          <Button
            onClick={() => setActiveTab('transactions')}
            variant={activeTab === 'transactions' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Transakcje
          </Button>
          <Button
            onClick={() => setActiveTab('tax-data')}
            variant={activeTab === 'tax-data' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Database className="w-4 h-4 mr-2" />
            Dane Podatkowe
          </Button>
          <Button
            onClick={() => setActiveTab('law-changes')}
            variant={activeTab === 'law-changes' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Scale className="w-4 h-4 mr-2" />
            Przepisy
          </Button>
          <Button
            onClick={() => setActiveTab('emails')}
            variant={activeTab === 'emails' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Globe className="w-4 h-4 mr-2" />
            Email
          </Button>
          <Button
            onClick={() => setActiveTab('daily-questions')}
            variant={activeTab === 'daily-questions' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Pytanie Dnia
          </Button>
          <Button
            onClick={() => setActiveTab('verification')}
            variant={activeTab === 'verification' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Shield className="w-4 h-4 mr-2" />
            Weryfikacja AI
          </Button>
          <Button
            onClick={() => { setActiveTab('ai-monitoring'); loadMonitoringData(1, ''); }}
            variant={activeTab === 'ai-monitoring' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Eye className="w-4 h-4 mr-2" />
            Monitoring AI
          </Button>
          <Button
            data-testid="tab-quality"
            onClick={() => { setActiveTab('quality'); loadQualityData(); }}
            variant={activeTab === 'quality' ? 'default' : 'ghost'}
            className="flex-1 min-w-[120px]"
          >
            <Shield className="w-4 h-4 mr-2" />
            Jakość AI
          </Button>
          <Button
            onClick={() => setShowFreeAccountDialog(true)}
            variant="outline"
            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
          >
            <Gift className="w-4 h-4 mr-2" />
            Darmowe Konto
          </Button>
          <Button
            onClick={handleSyncModels}
            variant="outline"
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Modele AI
          </Button>
        </div>

        {/* Users Table */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-card p-6 mb-8">
          <h2 className="font-heading text-2xl text-primary-900 mb-6">Użytkownicy</h2>
          <ScrollArea className="h-[400px]">
            <table className="w-full">
              <thead className="border-b border-slate-200">
                <tr className="text-left">
                  <th className="pb-3 text-sm font-semibold text-slate-700">Email</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Imię i nazwisko</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Rola</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Status subskrypcji</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Data rejestracji</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100" data-testid={`user-row-${user.id}`}>
                    <td className="py-3 text-sm">{user.email}</td>
                    <td className="py-3 text-sm">{user.full_name}</td>
                    <td className="py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3">
                      {user.subscription ? (
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            user.subscription.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : user.subscription.status === 'trial'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {user.subscription.status}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">brak</span>
                      )}
                    </td>
                    <td className="py-3 text-sm text-slate-600">
                      {new Date(user.created_at).toLocaleDateString('pl-PL')}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditSubscription(user)}
                          data-testid={`edit-sub-${user.id}`}
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          Edytuj
                        </Button>
                        {user.role !== 'admin' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePromoteToAdmin(user.id, user.email)}
                              className="text-purple-600 hover:text-purple-700"
                              data-testid={`promote-admin-${user.id}`}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </div>
        )}

        {/* Knowledge Base Tab */}
        {activeTab === 'knowledge' && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h2 className="font-heading text-2xl text-primary-900 mb-6">Przesyłanie Dokumentów do Bazy Wiedzy</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="knowledge-title" className="text-sm font-semibold">Tytuł dokumentu</Label>
                  <Input
                    id="knowledge-title"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Np. Ustawa o rachunkowości 2026"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="knowledge-file" className="text-sm font-semibold">Plik (PDF, TXT, DOCX, JSON, XML, CSV, MD, HTML)</Label>
                  <Input
                    id="knowledge-file"
                    type="file"
                    accept=".pdf,.txt,.docx,.doc,.json,.xml,.csv,.md,.html"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="mt-2"
                  />
                </div>
                <Button
                  onClick={handleUploadKnowledge}
                  disabled={!uploadFile || !uploadTitle}
                  className="bg-primary hover:bg-primary-900 text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Prześlij Plik
                </Button>
              </div>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-4 text-muted-foreground">lub dodaj link</span>
                </div>
              </div>

              {/* URL Input Section */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="url-input" className="text-sm font-semibold flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Link do strony internetowej
                  </Label>
                  <Input
                    id="url-input"
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/artykul"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="url-title" className="text-sm font-semibold">Tytuł (opcjonalnie)</Label>
                  <Input
                    id="url-title"
                    value={urlTitle}
                    onChange={(e) => setUrlTitle(e.target.value)}
                    placeholder="Tytuł zostanie pobrany automatycznie"
                    className="mt-2"
                  />
                </div>
                <Button
                  onClick={handleAddUrlKnowledge}
                  disabled={!urlInput || isLoadingUrl}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isLoadingUrl ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Pobieranie strony...
                    </>
                  ) : (
                    <>
                      <Link className="w-4 h-4 mr-2" />
                      Dodaj Link do Bazy Wiedzy
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Knowledge List */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl text-primary-900">Dokumenty w Bazie Wiedzy</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{knowledgeBase.length} dokumentów</span>
                  <Button
                    data-testid="refresh-knowledge-btn"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        toast.info('Odświeżanie bazy wiedzy...');
                        const resp = await axios.post(`${API_URL}/admin/knowledge/refresh`, { portal: 'all' }, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          timeout: 120000
                        });
                        toast.success(resp.data.message);
                        loadAdminData();
                      } catch (err) {
                        toast.error('Błąd odświeżania bazy');
                      }
                    }}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Odśwież bazę
                  </Button>
                  <Button
                    data-testid="deep-refresh-knowledge-btn"
                    variant="default"
                    size="sm"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        toast.info('Głębokie skanowanie portali... To może potrwać kilka minut.');
                        const resp = await axios.post(`${API_URL}/admin/knowledge/deep-refresh`, { portal: 'all' }, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          timeout: 300000
                        });
                        toast.success(resp.data.message);
                        loadAdminData();
                      } catch (err) {
                        toast.error('Błąd głębokiego skanowania');
                      }
                    }}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Search className="w-4 h-4" />
                    Głębokie skanowanie
                  </Button>
                </div>
              </div>
              {knowledgeBase.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <p className="text-muted-foreground">Brak dokumentów w bazie wiedzy</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <table className="w-full">
                    <thead className="border-b border-slate-200">
                      <tr className="text-left">
                        <th className="pb-3 text-sm font-semibold text-slate-700">Typ</th>
                        <th className="pb-3 text-sm font-semibold text-slate-700">Tytuł</th>
                        <th className="pb-3 text-sm font-semibold text-slate-700">Źródło</th>
                        <th className="pb-3 text-sm font-semibold text-slate-700">Rozmiar</th>
                        <th className="pb-3 text-sm font-semibold text-slate-700">Data</th>
                        <th className="pb-3 text-sm font-semibold text-slate-700">Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {knowledgeBase.map((doc) => (
                        <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3">
                            {doc.source_type === 'url' ? (
                              <Globe className="w-4 h-4 text-green-600" title="Link" />
                            ) : doc.portal ? (
                              <BookOpen className="w-4 h-4 text-purple-600" title={`Portal: ${doc.portal}`} />
                            ) : (
                              <FileText className="w-4 h-4 text-blue-600" title="Plik" />
                            )}
                          </td>
                          <td className="py-3 text-sm font-medium max-w-[200px] truncate" title={doc.title}>{doc.title}</td>
                          <td className="py-3 text-sm text-xs max-w-[150px] truncate">
                            {doc.portal ? (
                              <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">{doc.portal}</span>
                            ) : doc.source_type === 'url' ? (
                              <a href={doc.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1" title={doc.source_url}>
                                <span className="truncate">{doc.source_url?.substring(0, 30)}...</span>
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            ) : (
                              <span className="text-slate-600">{doc.file_name}</span>
                            )}
                          </td>
                          <td className="py-3 text-sm text-slate-600">{(doc.file_size / 1024).toFixed(1)} KB</td>
                          <td className="py-3 text-sm text-slate-600">{new Date(doc.created_at).toLocaleDateString('pl-PL')}</td>
                          <td className="py-3 flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePreviewDocument(doc)}
                              title="Podgląd dokumentu"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteKnowledge(doc.id)}
                              title="Usuń dokument"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </div>
          </div>
        )}

        {/* Portals Configuration Tab */}
        {activeTab === 'portals' && (
          <div className="space-y-6">
            {/* Knowledge Base Sources */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-heading text-2xl text-primary-900">Zrodla Bazy Wiedzy</h2>
                {knowledgeSources && (
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span data-testid="total-documents-count"><Database className="w-4 h-4 inline mr-1" />{knowledgeSources.total_documents} dokumentow</span>
                    <span><Scale className="w-4 h-4 inline mr-1" />{knowledgeSources.total_law_changes} zmian w przepisach</span>
                  </div>
                )}
              </div>
              <p className="text-muted-foreground mb-6">
                Automatyczne zrodla danych, z ktorych KAI czerpie wiedze. Scrapery pobieraja dane wg harmonogramu.
              </p>

              {knowledgeSources?.sources ? (
                <div className="grid gap-4" data-testid="knowledge-sources-list">
                  {knowledgeSources.sources.map((source, idx) => (
                    <div
                      key={idx}
                      data-testid={`source-card-${idx}`}
                      className={`border rounded-lg p-4 ${
                        source.status === 'active' ? 'border-green-200 bg-green-50/50' :
                        source.status === 'planned' ? 'border-amber-200 bg-amber-50/50' :
                        'border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <Globe className={`w-6 h-6 mt-0.5 ${
                            source.status === 'active' ? 'text-green-600' : 'text-amber-500'
                          }`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-base" data-testid={`source-name-${idx}`}>{source.name}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                source.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`} data-testid={`source-status-${idx}`}>
                                {source.status === 'active' ? 'Aktywny' : 'Planowany'}
                              </span>
                              {source.documents > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid={`source-docs-${idx}`}>
                                  {source.documents} dok.
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{source.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                {source.url} <ExternalLink className="w-3 h-3" />
                              </a>
                              <span>Harmonogram: {source.schedule}</span>
                              {source.last_update && (
                                <span>Ostatnia aktualizacja: {new Date(source.last_update).toLocaleDateString('pl-PL')}</span>
                              )}
                            </div>
                            {source.by_category && Object.keys(source.by_category).length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {Object.entries(source.by_category).map(([cat, count]) => (
                                  <span key={cat} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                    {cat}: {count}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          {source.api_endpoint && source.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`refresh-source-${idx}`}
                              disabled={refreshingSource === source.name}
                              onClick={() => handleRefreshSource(source.api_endpoint, source.name)}
                            >
                              {refreshingSource === source.name ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Odswiez
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">Ladowanie zrodel...</p>
                </div>
              )}
            </div>

            {/* Portal Credentials Section */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h2 className="font-heading text-xl text-primary-900 mb-2">Dane Logowania do Portali</h2>
              <p className="text-muted-foreground mb-6">
                Dodaj dane logowania do portali, aby KAI mogl pobierac pelne tresci artykulow.
              </p>
              
              <div className="grid gap-4">
                {portalCredentials.map((portal) => (
                  <div 
                    key={portal.portal_name}
                    className={`border rounded-lg p-4 ${portal.configured ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Globe className={`w-5 h-5 ${portal.configured ? 'text-green-600' : 'text-slate-400'}`} />
                        <div>
                          <h3 className="font-semibold">{portal.portal_display_name}</h3>
                          {portal.configured && (
                            <p className="text-sm text-green-600">
                              Zalogowano jako: {portal.username}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {portal.configured ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestPortal(portal.portal_name)}
                              disabled={testingPortal === portal.portal_name}
                            >
                              {testingPortal === portal.portal_name ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Test
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeletePortalCredentials(portal.portal_name)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-sm text-slate-500">Nieskonfigurowany</span>
                        )}
                      </div>
                    </div>
                    
                    {selectedPortal === portal.portal_name ? (
                      <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <Label>Login / Email</Label>
                            <Input
                              type="text"
                              placeholder="Twoj login"
                              value={portalForm.username}
                              onChange={(e) => setPortalForm({...portalForm, username: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Haslo</Label>
                            <Input
                              type="password"
                              placeholder="Twoje haslo"
                              value={portalForm.password}
                              onChange={(e) => setPortalForm({...portalForm, password: e.target.value})}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleSavePortalCredentials(portal.portal_name)}
                            disabled={savingPortal}
                          >
                            {savingPortal ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : null}
                            Zapisz
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setSelectedPortal(null);
                              setPortalForm({ username: '', password: '' });
                            }}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedPortal(portal.portal_name);
                          setPortalForm({ 
                            username: portal.username || '', 
                            password: '' 
                          });
                        }}
                      >
                        {portal.configured ? 'Zmien dane logowania' : 'Skonfiguruj'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        {activeTab === 'transactions' && (
          <div className="bg-white rounded-lg shadow-card p-6">
          <h2 className="font-heading text-2xl text-primary-900 mb-6">Historia Płatności</h2>
          <ScrollArea className="h-[300px]">
            <table className="w-full">
              <thead className="border-b border-slate-200">
                <tr className="text-left">
                  <th className="pb-3 text-sm font-semibold text-slate-700">Data</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Kwota</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Status</th>
                  <th className="pb-3 text-sm font-semibold text-slate-700">Session ID</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((trans) => (
                  <tr key={trans.id} className="border-b border-slate-100">
                    <td className="py-3 text-sm">
                      {new Date(trans.created_at).toLocaleString('pl-PL')}
                    </td>
                    <td className="py-3 text-sm font-semibold">
                      {trans.amount.toFixed(2)} {trans.currency.toUpperCase()}
                    </td>
                    <td className="py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          trans.payment_status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : trans.payment_status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {trans.payment_status}
                      </span>
                    </td>
                    <td className="py-3 text-xs text-slate-600 font-mono">{trans.session_id.substring(0, 20)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </div>
        )}

        {/* Critical Tax Data Tab */}
        {activeTab === 'tax-data' && (
          <div className="bg-white rounded-lg shadow-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl text-primary-900">Krytyczne Dane Podatkowe</h2>
                <p className="text-sm text-slate-500 mt-1">Zweryfikowane limity i progi - mają najwyższy priorytet w odpowiedziach AI</p>
              </div>
              <Button
                data-testid="add-tax-data-btn"
                onClick={() => {
                  setEditingTaxData(null);
                  setTaxDataForm({ name: '', value: '', category: 'VAT', year: new Date().getFullYear(), legal_basis: '', notes: '' });
                  setShowTaxDataDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Dodaj
              </Button>
            </div>

            {/* Group by category */}
            {(() => {
              const categories = [...new Set(criticalTaxData.map(d => d.category))].sort();
              return categories.map(category => (
                <div key={category} className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 border-b pb-2">{category}</h3>
                  <div className="space-y-2">
                    {criticalTaxData
                      .filter(d => d.category === category)
                      .sort((a, b) => b.year - a.year)
                      .map(entry => (
                        <div
                          key={entry.id}
                          data-testid={`tax-data-entry-${entry.id}`}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            entry.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{entry.name}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{entry.year}</span>
                              {!entry.active && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">nieaktywne</span>}
                            </div>
                            <div className="text-lg font-bold text-primary-900 mt-0.5">{entry.value}</div>
                            {entry.legal_basis && (
                              <div className="text-xs text-slate-500 mt-0.5">{entry.legal_basis}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`toggle-tax-${entry.id}`}
                              onClick={() => handleToggleTaxDataActive(entry)}
                              title={entry.active ? 'Dezaktywuj' : 'Aktywuj'}
                            >
                              {entry.active ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-tax-${entry.id}`}
                              onClick={() => handleEditTaxData(entry)}
                            >
                              <Edit className="w-4 h-4 text-slate-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-tax-${entry.id}`}
                              onClick={() => handleDeleteTaxData(entry.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ));
            })()}

            {criticalTaxData.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Database className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Brak danych podatkowych. Kliknij "Dodaj" aby wprowadzić limity i progi.</p>
              </div>
            )}
          </div>
        )}

        {/* Law Changes Tab */}
        {activeTab === 'law-changes' && (
          <div className="bg-white rounded-lg shadow-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl text-primary-900">Zmiany w Przepisach</h2>
                <p className="text-sm text-slate-500 mt-1">Zarządzaj informacjami o zmianach prawnych</p>
              </div>
              <Button
                data-testid="add-law-change-btn"
                onClick={() => {
                  setEditingLawChange(null);
                  setLawChangeForm({ title: '', description: '', severity: 'info', effective_date: '', legal_basis: '', category: '', who_affected: '', action_required: '', consequences: '', deadline: '' });
                  setShowLawChangeDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Dodaj zmianę
              </Button>
            </div>

            <div className="space-y-3">
              {lawChanges.map((change, idx) => (
                <div key={change.id || idx} className={`p-4 rounded-lg border flex items-start justify-between ${
                  change.severity === 'critical' ? 'bg-red-50 border-red-200' :
                  change.severity === 'important' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start gap-3 flex-1">
                    {change.severity === 'critical' ? <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" /> :
                     change.severity === 'important' ? <Info className="w-5 h-5 text-yellow-500 mt-0.5" /> :
                     <Scale className="w-5 h-5 text-blue-500 mt-0.5" />}
                    <div>
                      <h3 className="font-medium text-sm">{change.title}</h3>
                      <p className="text-xs text-slate-600 mt-1">{change.description?.substring(0, 150)}...</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {change.effective_date && <span className="text-xs text-slate-500">Od: {change.effective_date}</span>}
                        {change.category && <span className="text-xs px-2 py-0.5 rounded-full bg-white/50">{change.category}</span>}
                        {change.who_affected && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">👥 {change.who_affected.substring(0, 30)}...</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`edit-law-change-${idx}`}
                      onClick={() => {
                        setEditingLawChange(change);
                        setLawChangeForm({
                          title: change.title || '',
                          description: change.description || '',
                          severity: change.severity || 'info',
                          effective_date: change.effective_date || '',
                          legal_basis: change.legal_basis || '',
                          category: change.category || '',
                          who_affected: change.who_affected || '',
                          action_required: change.action_required || '',
                          consequences: change.consequences || '',
                          deadline: change.deadline || ''
                        });
                        setShowLawChangeDialog(true);
                      }}
                    >
                      <Edit className="w-4 h-4 text-blue-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-law-change-${idx}`}
                      onClick={async () => {
                        if (!window.confirm('Usunąć tę zmianę?')) return;
                        try {
                          const token = localStorage.getItem('token');
                          await axios.delete(`${API_URL}/admin/law-changes/${change.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Usunięto');
                          loadAdminData();
                        } catch { toast.error('Błąd'); }
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {lawChanges.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Scale className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Brak zmian w przepisach. Kliknij "Dodaj zmianę".</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-6">
            {/* Send Notification */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h2 className="font-heading text-2xl text-primary-900 mb-6">Wyślij powiadomienie email</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Temat</Label>
                  <Input value={emailForm.subject} onChange={(e) => setEmailForm(f => ({ ...f, subject: e.target.value }))} placeholder="Temat wiadomości..." data-testid="email-subject" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Treść</Label>
                  <textarea
                    className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md text-sm"
                    value={emailForm.message}
                    onChange={(e) => setEmailForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Treść wiadomości..."
                    data-testid="email-message"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Odbiorcy</Label>
                  <Select value={emailForm.target} onValueChange={(v) => setEmailForm(f => ({ ...f, target: v }))}>
                    <SelectTrigger data-testid="email-target"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Wszyscy użytkownicy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="send-email-btn"
                  disabled={sendingEmail || !emailForm.subject || !emailForm.message}
                  onClick={async () => {
                    setSendingEmail(true);
                    try {
                      const token = localStorage.getItem('token');
                      const resp = await axios.post(`${API_URL}/admin/notifications/send`, {
                        title: emailForm.subject,
                        message: emailForm.message,
                        target: emailForm.target,
                        severity: 'info',
                      }, {
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                      });
                      toast.success(resp.data.message);
                      setEmailForm({ subject: '', message: '', target: 'all' });
                    } catch { toast.error('Blad wysylania'); }
                    finally { setSendingEmail(false); }
                  }}
                >
                  {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                  Wyslij powiadomienie
                </Button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h2 className="font-heading text-2xl text-primary-900 mb-6">Szybkie akcje</h2>
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="send-reminders-btn"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const resp = await axios.post(`${API_URL}/admin/send-subscription-reminders`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(resp.data.message);
                    } catch { toast.error('Błąd wysyłania'); }
                  }}
                >
                  Wyślij przypomnienia o subskrypcji
                </Button>
                <Button
                  data-testid="monitor-laws-btn"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      toast.info('Skanowanie portali... może potrwać do 2 minut');
                      const resp = await axios.post(`${API_URL}/admin/monitor-laws`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                        timeout: 180000
                      });
                      toast.success(resp.data.message);
                      loadAdminData();
                    } catch { toast.error('Błąd skanowania'); }
                  }}
                >
                  Skanuj zmiany w przepisach (AI)
                </Button>
                <Button
                  data-testid="send-newsletter-btn"
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      toast.info('Wysylanie newslettera jako powiadomienie...');
                      const resp = await axios.post(`${API_URL}/admin/notifications/send-newsletter`, {
                        title: '',
                        intro: ''
                      }, {
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        timeout: 30000
                      });
                      toast.success(resp.data.message);
                    } catch { toast.error('Blad wysylania newslettera'); }
                  }}
                >
                  Wyslij newsletter (powiadomienie in-app)
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Daily Questions Tab */}
        {activeTab === 'daily-questions' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl text-primary-900">Pytanie Dnia</h2>
                <Button
                  data-testid="add-daily-question-btn"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setDailyQuestionForm({
                      question: '',
                      category: 'VAT',
                      hint: '',
                      date: tomorrow.toISOString().split('T')[0]
                    });
                    setShowDailyQuestionDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj pytanie
                </Button>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Pytania dnia generowane są automatycznie przez AI na podstawie aktualnych terminów i zmian w przepisach.
                Możesz też ręcznie dodać pytanie na wybrany dzień — pytanie admina ma priorytet nad AI.
              </p>
              {dailyQuestions.length === 0 ? (
                <p className="text-center py-8 text-slate-400">Brak zapisanych pytań</p>
              ) : (
                <div className="space-y-3">
                  {dailyQuestions.map((q, idx) => (
                    <div
                      key={idx}
                      data-testid={`daily-question-item-${idx}`}
                      className="border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full">
                            {q.date}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                            {q.category}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            q.source === 'ai' ? 'bg-blue-100 text-blue-600' : q.source === 'admin' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {q.source === 'ai' ? 'AI' : q.source === 'admin' ? 'Admin' : 'Fallback'}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-800">{q.question}</p>
                        {q.hint && <p className="text-xs text-slate-500 mt-1">{q.hint}</p>}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-question-${idx}`}
                          onClick={() => {
                            setDailyQuestionForm({
                              question: q.question,
                              category: q.category || 'VAT',
                              hint: q.hint || '',
                              date: q.date
                            });
                            setShowDailyQuestionDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-question-${idx}`}
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await axios.delete(`${API_URL}/admin/daily-questions/${q.date}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Pytanie usunięte');
                              setDailyQuestions(prev => prev.filter(x => x.date !== q.date));
                            } catch { toast.error('Błąd usuwania'); }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verification Stats Tab */}
        {activeTab === 'verification' && verificationStats && (
          <div className="space-y-6">
            {/* Overview cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-card p-4 text-center">
                <div className="text-3xl font-bold text-primary-900">{verificationStats.overview?.total_verified || 0}</div>
                <div className="text-xs text-slate-500 mt-1">Zweryfikowane</div>
              </div>
              <div className="bg-white rounded-lg shadow-card p-4 text-center">
                <div className={`text-3xl font-bold ${(verificationStats.overview?.accuracy_rate || 0) >= 80 ? 'text-green-600' : (verificationStats.overview?.accuracy_rate || 0) >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {verificationStats.overview?.accuracy_rate || 0}%
                </div>
                <div className="text-xs text-slate-500 mt-1">Poprawność</div>
              </div>
              <div className="bg-white rounded-lg shadow-card p-4 text-center">
                <div className="text-3xl font-bold text-amber-600">{verificationStats.overview?.total_corrections || 0}</div>
                <div className="text-xs text-slate-500 mt-1">Autokorekty</div>
              </div>
              <div className="bg-white rounded-lg shadow-card p-4 text-center">
                <div className="text-3xl font-bold text-slate-400">{verificationStats.overview?.total_unverified || 0}</div>
                <div className="text-xs text-slate-500 mt-1">Bez weryfikacji</div>
              </div>
            </div>

            {/* Sources breakdown */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="font-heading text-lg text-primary-900 mb-4">Źródła weryfikacji</h3>
              <div className="space-y-3">
                {[
                  { label: 'Baza wiedzy + Internet', count: verificationStats.sources?.multi_source || 0, color: 'bg-green-500' },
                  { label: 'Tylko baza wiedzy', count: verificationStats.sources?.knowledge_base || 0, color: 'bg-blue-500' },
                  { label: 'Tylko internet', count: verificationStats.sources?.web_search || 0, color: 'bg-cyan-500' },
                  { label: 'Bez źródeł', count: verificationStats.sources?.unverified || 0, color: 'bg-slate-300' },
                ].map((s, i) => {
                  const total = (verificationStats.overview?.total_verified || 1);
                  const pct = Math.round((s.count / total) * 100);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-32 text-sm text-slate-600 flex-shrink-0">{s.label}</div>
                      <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                        <div className={`h-full ${s.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-sm font-medium text-slate-700 w-16 text-right">{s.count} ({pct}%)</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Background verification results */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="font-heading text-lg text-primary-900 mb-4">Weryfikacja w tle</h3>
              <div className="flex gap-6 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm">Poprawne: <strong>{verificationStats.background_check?.correct || 0}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm">Z problemami: <strong>{verificationStats.background_check?.incorrect || 0}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <span className="text-sm">Oczekujące: <strong>{verificationStats.background_check?.pending || 0}</strong></span>
                </div>
              </div>
            </div>

            {/* Recent issues */}
            {verificationStats.recent_issues?.length > 0 && (
              <div className="bg-white rounded-lg shadow-card p-6">
                <h3 className="font-heading text-lg text-red-700 mb-4">Ostatnie problemy</h3>
                <div className="space-y-3">
                  {verificationStats.recent_issues.map((issue, idx) => (
                    <div key={idx} data-testid={`verification-issue-${idx}`} className="border border-red-100 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-400">{issue.timestamp?.split('T')[0]}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                          Pewność: {Math.round((issue.verification?.result?.confidence || 0) * 100)}%
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{issue.content}</p>
                      {issue.verification?.result?.issues?.length > 0 && (
                        <ul className="mt-2 text-xs text-red-600 list-disc pl-4">
                          {issue.verification.result.issues.map((i, j) => <li key={j}>{i}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent corrections */}
            {verificationStats.recent_corrections?.length > 0 && (
              <div className="bg-white rounded-lg shadow-card p-6">
                <h3 className="font-heading text-lg text-amber-700 mb-4">Ostatnie autokorekty</h3>
                <div className="space-y-3">
                  {verificationStats.recent_corrections.map((corr, idx) => (
                    <div key={idx} data-testid={`verification-correction-${idx}`} className="border border-amber-100 rounded-lg p-3">
                      <span className="text-xs text-slate-400">{corr.timestamp?.split('T')[0]}</span>
                      <p className="text-sm text-slate-700 mt-1">{corr.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'verification' && !verificationStats && (
          <div className="text-center py-12 text-slate-400">Brak danych weryfikacji</div>
        )}

        {/* ============ AI MONITORING TAB ============ */}
        {activeTab === 'ai-monitoring' && (
          <div className="space-y-6">
            {/* Dashboard Cards */}
            {aiMonitoringDashboard && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {[
                    { label: 'Rozmowy', value: aiMonitoringDashboard.totals?.conversations || 0, color: 'text-primary-900' },
                    { label: 'Odpowiedzi AI', value: aiMonitoringDashboard.totals?.ai_responses || 0, color: 'text-blue-600' },
                    { label: 'Pytania', value: aiMonitoringDashboard.totals?.user_messages || 0, color: 'text-slate-700' },
                    { label: 'Dziś', value: aiMonitoringDashboard.totals?.today_responses || 0, color: 'text-green-600' },
                    { label: 'Aktywni (7d)', value: aiMonitoringDashboard.totals?.active_users_week || 0, color: 'text-purple-600' },
                    { label: 'Poprawność', value: `${aiMonitoringDashboard.totals?.accuracy_rate || 0}%`, color: aiMonitoringDashboard.totals?.accuracy_rate >= 80 ? 'text-green-600' : 'text-amber-600' },
                    { label: 'Korekty', value: aiMonitoringDashboard.totals?.corrections || 0, color: 'text-amber-600' },
                  ].map((card, i) => (
                    <div key={i} className="bg-white rounded-lg shadow-card p-4 text-center" data-testid={`monitoring-card-${i}`}>
                      <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
                      <div className="text-xs text-slate-500 mt-1">{card.label}</div>
                    </div>
                  ))}
                </div>

                {/* Daily Chart */}
                {aiMonitoringDashboard.daily_stats?.length > 0 && (
                  <div className="bg-white rounded-lg shadow-card p-6">
                    <h3 className="font-heading text-lg text-primary-900 mb-4">Aktywność AI (14 dni)</h3>
                    <div className="flex items-end gap-1 h-32">
                      {aiMonitoringDashboard.daily_stats.map((day, i) => {
                        const max = Math.max(...aiMonitoringDashboard.daily_stats.map(d => d.count), 1);
                        const height = Math.max((day.count / max) * 100, 2);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-[10px] text-slate-500">{day.count || ''}</span>
                            <div
                              className="w-full bg-blue-500 rounded-t-sm transition-all hover:bg-blue-600"
                              style={{ height: `${height}%` }}
                              title={`${day.date}: ${day.count} odpowiedzi`}
                            />
                            <span className="text-[9px] text-slate-400">{day.date}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sources + Topics row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sources */}
                  <div className="bg-white rounded-lg shadow-card p-6">
                    <h3 className="font-heading text-lg text-primary-900 mb-4">Źródła odpowiedzi</h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Baza + Internet', count: aiMonitoringDashboard.sources?.multi_source || 0, color: 'bg-green-500' },
                        { label: 'Baza wiedzy', count: aiMonitoringDashboard.sources?.knowledge_base || 0, color: 'bg-blue-500' },
                        { label: 'Internet', count: aiMonitoringDashboard.sources?.web_search || 0, color: 'bg-cyan-500' },
                        { label: 'Bez źródeł', count: aiMonitoringDashboard.sources?.unverified || 0, color: 'bg-slate-300' },
                      ].map((s, i) => {
                        const total = (aiMonitoringDashboard.totals?.ai_responses || 1);
                        const pct = Math.round((s.count / total) * 100);
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-28 text-sm text-slate-600 flex-shrink-0">{s.label}</div>
                            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                              <div className={`h-full ${s.color} rounded-full`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-xs font-medium text-slate-600 w-14 text-right">{s.count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Top Topics */}
                  <div className="bg-white rounded-lg shadow-card p-6">
                    <h3 className="font-heading text-lg text-primary-900 mb-4">Popularne tematy</h3>
                    <div className="space-y-2">
                      {(aiMonitoringDashboard.top_topics || []).map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-sm" data-testid={`topic-${i}`}>
                          <span className="text-slate-700">{t.topic}</span>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t.count}</span>
                        </div>
                      ))}
                      {(!aiMonitoringDashboard.top_topics || aiMonitoringDashboard.top_topics.length === 0) && (
                        <p className="text-sm text-slate-400">Brak danych o tematach</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Conversations Table */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg text-primary-900">Rozmowy ({monitoringTotal})</h3>
                <div className="flex gap-2">
                  <Input
                    placeholder="Szukaj (email, temat)..."
                    value={monitoringSearch}
                    onChange={(e) => setMonitoringSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setMonitoringPage(1); loadMonitoringData(1, monitoringSearch); }}}
                    className="w-64"
                    data-testid="monitoring-search-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => { setMonitoringPage(1); loadMonitoringData(1, monitoringSearch); }} data-testid="monitoring-search-btn">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="monitoring-conversations-table">
                  <thead>
                    <tr className="border-b text-left text-xs text-slate-500 uppercase">
                      <th className="pb-2 pr-3">Użytkownik</th>
                      <th className="pb-2 pr-3">Temat</th>
                      <th className="pb-2 pr-3 text-center">Pytania</th>
                      <th className="pb-2 pr-3 text-center">Odpowiedzi</th>
                      <th className="pb-2 pr-3 text-center">OK</th>
                      <th className="pb-2 pr-3 text-center">Błędy</th>
                      <th className="pb-2 pr-3">Ostatnia aktywność</th>
                      <th className="pb-2">Akcja</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monitoringConversations.map((conv, idx) => (
                      <tr key={conv.id || idx} className="border-b border-slate-50 hover:bg-slate-50 text-sm" data-testid={`monitoring-conv-${idx}`}>
                        <td className="py-2 pr-3">
                          <div className="font-medium text-slate-700 truncate max-w-[140px]">{conv.user_email}</div>
                          <div className="text-[10px] text-slate-400">{conv.user_name}</div>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="truncate max-w-[200px] text-slate-600">{conv.title}</div>
                        </td>
                        <td className="py-2 pr-3 text-center text-slate-600">{conv.user_messages}</td>
                        <td className="py-2 pr-3 text-center text-blue-600 font-medium">{conv.ai_messages}</td>
                        <td className="py-2 pr-3 text-center">
                          {conv.verified_ok > 0 && <span className="text-green-600 font-medium">{conv.verified_ok}</span>}
                        </td>
                        <td className="py-2 pr-3 text-center">
                          {conv.verified_fail > 0 && <span className="text-red-600 font-medium">{conv.verified_fail}</span>}
                        </td>
                        <td className="py-2 pr-3 text-xs text-slate-400">
                          {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString('pl-PL') : '-'}
                        </td>
                        <td className="py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadConversationDetail(conv.id)}
                            disabled={loadingConversation}
                            data-testid={`view-conv-${idx}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {monitoringConversations.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-slate-400">Brak rozmów</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {monitoringPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" disabled={monitoringPage <= 1} onClick={() => { setMonitoringPage(p => p - 1); loadMonitoringData(monitoringPage - 1, monitoringSearch); }}>Wstecz</Button>
                  <span className="text-sm text-slate-500">Strona {monitoringPage} z {monitoringPages}</span>
                  <Button variant="outline" size="sm" disabled={monitoringPage >= monitoringPages} onClick={() => { setMonitoringPage(p => p + 1); loadMonitoringData(monitoringPage + 1, monitoringSearch); }}>Dalej</Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quality / Golden Set Panel */}
        {activeTab === 'quality' && (
          <div className="space-y-6" data-testid="quality-panel">
            {/* Summary / Search Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-card p-5">
                <div className="text-xs text-slate-500 uppercase">Baza wiedzy</div>
                <div className="text-2xl font-bold text-emerald-700 mt-1">
                  {searchStats?.total_kb_docs ?? 0} <span className="text-sm font-normal text-slate-500">dok.</span>
                </div>
                <div className="text-xs text-slate-500 mt-2">{searchStats?.search_method || 'BM25'}</div>
              </div>
              <div className="bg-white rounded-lg shadow-card p-5">
                <div className="text-xs text-slate-500 uppercase">Ostatni test jakości</div>
                <div className="text-2xl font-bold text-blue-700 mt-1">
                  {goldenRun ? `${goldenRun.passed}/${goldenRun.total_cases}` : '—'}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {goldenRun ? `Średnia ${goldenRun.avg_score}/100` : 'Nie uruchomiono'}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-card p-5">
                <div className="text-xs text-slate-500 uppercase">Przypadków testowych</div>
                <div className="text-2xl font-bold text-indigo-700 mt-1">{goldenCases.length}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={seedGoldenCases} data-testid="seed-golden-btn">
                    Zasiej domyślne
                  </Button>
                  <Button size="sm" onClick={runGoldenSet} disabled={runningGolden || goldenCases.length === 0} data-testid="run-golden-btn">
                    {runningGolden ? 'Uruchamiam...' : 'Uruchom testy'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Golden Set Manager */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Golden Set — testy regresyjne jakości AI
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Zdefiniuj kanoniczne pytania z oczekiwanymi faktami. System uruchomi je przeciwko obecnemu pipelinowi i pokaże, czy odpowiedzi zawierają wszystkie kluczowe dane.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <Input
                  placeholder="Pytanie"
                  value={newGoldenCase.question}
                  onChange={(e) => setNewGoldenCase(c => ({ ...c, question: e.target.value }))}
                  data-testid="new-golden-question"
                />
                <textarea
                  className="w-full min-h-[40px] px-3 py-2 border border-slate-200 rounded-md text-sm"
                  placeholder="Oczekiwane fakty (jeden na linię)"
                  value={newGoldenCase.expected_facts}
                  onChange={(e) => setNewGoldenCase(c => ({ ...c, expected_facts: e.target.value }))}
                  data-testid="new-golden-facts"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Temat (VAT / PIT / ...)"
                    value={newGoldenCase.topic}
                    onChange={(e) => setNewGoldenCase(c => ({ ...c, topic: e.target.value }))}
                    data-testid="new-golden-topic"
                  />
                  <Button onClick={createGoldenCase} data-testid="add-golden-btn">Dodaj</Button>
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {goldenCases.map(c => (
                  <div key={c.id} className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-md">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{c.question}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Temat: <span className="font-medium">{c.topic || '—'}</span>
                        {' • '}
                        Fakty: {(c.expected_facts || []).join(' | ')}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteGoldenCase(c.id)} className="text-red-600" data-testid={`delete-golden-${c.id}`}>
                      Usuń
                    </Button>
                  </div>
                ))}
                {goldenCases.length === 0 && (
                  <div className="text-center py-6 text-slate-500 text-sm">
                    Brak przypadków testowych. Zasiej domyślne lub dodaj nowy.
                  </div>
                )}
              </div>
            </div>

            {/* Golden Run Results */}
            {goldenRun && goldenRun.results && (
              <div className="bg-white rounded-lg shadow-card p-6">
                <h3 className="text-lg font-bold mb-4">Wyniki ostatniego testu</h3>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {goldenRun.results.map((r, i) => (
                    <div key={i} className={`p-3 rounded-md border-l-4 ${r.passed ? 'bg-emerald-50 border-emerald-500' : 'bg-rose-50 border-rose-500'}`}>
                      <div className="flex justify-between items-start mb-1">
                        <div className="font-medium text-sm flex-1">{r.question}</div>
                        <span className={`text-xs font-bold ${r.passed ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {r.score}/100 {r.passed ? '✓' : '✗'}
                        </span>
                      </div>
                      {r.missed_facts && r.missed_facts.length > 0 && (
                        <div className="text-xs text-rose-700 mt-1">
                          <span className="font-medium">Brakujące:</span> {r.missed_facts.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* KB Gaps */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-bold mb-4">Luki w bazie wiedzy (audit-driven)</h3>
              <p className="text-sm text-slate-600 mb-3">Tematy, w których AI odpowiadało bez pokrycia w bazie wiedzy — priorytetowe do uzupełnienia.</p>
              {kbGaps.length > 0 ? (
                <div className="space-y-2">
                  {kbGaps.map((g, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-amber-50 rounded-md border border-amber-200">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{g.topic}</div>
                        {g.sample_queries && g.sample_queries.length > 0 && (
                          <div className="text-xs text-slate-600 mt-1 line-clamp-2">
                            Przykłady: {g.sample_queries.join(' | ')}
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-bold text-amber-700 ml-3">{g.missing_kb_hits}×</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500 text-sm">Brak wykrytych luk — wszystko OK</div>
              )}
            </div>

            {/* A/B stats */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-bold mb-4">A/B Test — warianty promptu</h3>
              <div className="grid grid-cols-2 gap-4">
                {['A', 'B'].map(v => (
                  <div key={v} className="p-4 bg-slate-50 rounded-md">
                    <div className="font-bold text-sm mb-2">Wariant {v}</div>
                    {Object.entries(abStats[v] || {}).length > 0 ? (
                      Object.entries(abStats[v]).map(([event, stats]) => (
                        <div key={event} className="text-xs text-slate-600 mb-1">
                          <span className="font-medium">{event}:</span> {stats.count} (avg conf: {stats.avg_confidence}, time: {stats.avg_response_time_ms}ms)
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-slate-400">Brak danych</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Low-quality queries */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-bold mb-4">Pytania z niską jakością (ostatnie 30 dni)</h3>
              {lowQualityQueries.length > 0 ? (
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  {lowQualityQueries.map((q, i) => (
                    <div key={i} className="flex justify-between items-start p-2 bg-rose-50 rounded text-xs">
                      <div className="flex-1 truncate">{q.query_preview}</div>
                      <span className="ml-2 text-rose-700 font-bold">{q.occurrences}×</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500 text-sm">Brak problemowych pytań</div>
              )}
            </div>
          </div>
        )}


      </div>
      <Dialog open={showDailyQuestionDialog} onOpenChange={setShowDailyQuestionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pytanie dnia</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Data</Label>
              <Input
                type="date"
                value={dailyQuestionForm.date}
                onChange={(e) => setDailyQuestionForm(f => ({ ...f, date: e.target.value }))}
                data-testid="dq-date-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Pytanie</Label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md text-sm"
                value={dailyQuestionForm.question}
                onChange={(e) => setDailyQuestionForm(f => ({ ...f, question: e.target.value }))}
                placeholder="Treść pytania..."
                data-testid="dq-question-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Kategoria</Label>
              <Select value={dailyQuestionForm.category} onValueChange={(v) => setDailyQuestionForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="dq-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['VAT', 'PIT', 'CIT', 'ZUS', 'Rachunkowość', 'Prawo pracy', 'KSeF', 'Ryczałt', 'Ordynacja', 'JPK', 'Transakcje', 'Ogólne'].map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Podpowiedź</Label>
              <Input
                value={dailyQuestionForm.hint}
                onChange={(e) => setDailyQuestionForm(f => ({ ...f, hint: e.target.value }))}
                placeholder="Krótka wskazówka..."
                data-testid="dq-hint-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDailyQuestionDialog(false)}>Anuluj</Button>
            <Button
              data-testid="dq-save-btn"
              disabled={!dailyQuestionForm.question || !dailyQuestionForm.date}
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(`${API_URL}/admin/daily-questions`, dailyQuestionForm, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                  });
                  toast.success('Pytanie zapisane');
                  setShowDailyQuestionDialog(false);
                  loadAdminData();
                } catch { toast.error('Błąd zapisu'); }
              }}
            >
              Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edytuj Subskrypcję</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Dodaj dni</label>
              <Input
                type="number"
                value={editForm.days_to_add}
                onChange={(e) => setEditForm({ ...editForm, days_to_add: parseInt(e.target.value) })}
                placeholder="Liczba dni"
                data-testid="days-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Anuluj
            </Button>
            <Button onClick={handleSaveSubscription} data-testid="save-subscription">
              Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Free Account Dialog */}
      <Dialog open={showFreeAccountDialog} onOpenChange={setShowFreeAccountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-600" />
              Utwórz Darmowe Konto
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="free-email" className="text-sm font-medium mb-2 block">Email</Label>
              <Input
                id="free-email"
                type="email"
                value={freeAccountForm.email}
                onChange={(e) => setFreeAccountForm({ ...freeAccountForm, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="free-name" className="text-sm font-medium mb-2 block">Imię i Nazwisko</Label>
              <Input
                id="free-name"
                value={freeAccountForm.full_name}
                onChange={(e) => setFreeAccountForm({ ...freeAccountForm, full_name: e.target.value })}
                placeholder="Jan Kowalski"
              />
            </div>
            <div>
              <Label htmlFor="free-password" className="text-sm font-medium mb-2 block">Hasło</Label>
              <Input
                id="free-password"
                type="password"
                value={freeAccountForm.password}
                onChange={(e) => setFreeAccountForm({ ...freeAccountForm, password: e.target.value })}
                placeholder="********"
              />
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              <strong>Info:</strong> Darmowe konto ma bezterminowy dostęp do aplikacji bez opłat.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFreeAccountDialog(false)}>
              Anuluj
            </Button>
            <Button 
              onClick={handleCreateFreeAccount} 
              disabled={creatingFreeAccount}
              className="bg-green-600 hover:bg-green-700"
            >
              {creatingFreeAccount ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Tworzenie...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Utwórz Konto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {previewDocument?.title || 'Podgląd dokumentu'}
            </DialogTitle>
          </DialogHeader>
          {previewDocument && (
            <div className="space-y-4">
              {/* Document metadata */}
              <div className="flex flex-wrap gap-3 text-sm">
                {previewDocument.portal && (
                  <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                    Portal: {previewDocument.portal}
                  </span>
                )}
                <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
                  Typ: {previewDocument.source_type || 'dokument'}
                </span>
                <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
                  Rozmiar: {(previewDocument.file_size / 1024).toFixed(1)} KB
                </span>
                <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
                  Dodano: {new Date(previewDocument.created_at).toLocaleDateString('pl-PL')}
                </span>
              </div>

              {/* Source URL */}
              {previewDocument.source_url && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-1">Źródło:</p>
                  <a 
                    href={previewDocument.source_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm break-all flex items-center gap-1"
                  >
                    {previewDocument.source_url}
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
              )}

              {/* Tags */}
              {previewDocument.tags && previewDocument.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-slate-600">Tagi:</span>
                  {previewDocument.tags.map((tag, i) => (
                    <span key={i} className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Content preview */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Treść dokumentu:</p>
                <ScrollArea className="h-[400px] border border-slate-200 rounded-lg p-4 bg-slate-50">
                  <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed">
                    {previewDocument.content || 'Brak treści do wyświetlenia'}
                  </pre>
                </ScrollArea>
              </div>
            </div>
          )}
          <DialogFooter>
            {previewDocument?.source_url && (
              <Button variant="outline" asChild>
                <a href={previewDocument.source_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Otwórz źródło
                </a>
              </Button>
            )}
            <Button onClick={() => setShowPreviewDialog(false)}>
              Zamknij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Critical Tax Data Dialog */}
      <Dialog open={showTaxDataDialog} onOpenChange={setShowTaxDataDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              {editingTaxData ? 'Edytuj dane podatkowe' : 'Dodaj dane podatkowe'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Nazwa (np. "Limit zwolnienia z VAT")</Label>
              <Input
                data-testid="tax-data-name"
                value={taxDataForm.name}
                onChange={(e) => setTaxDataForm({ ...taxDataForm, name: e.target.value })}
                placeholder="Limit zwolnienia podmiotowego z VAT"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Wartość</Label>
              <Input
                data-testid="tax-data-value"
                value={taxDataForm.value}
                onChange={(e) => setTaxDataForm({ ...taxDataForm, value: e.target.value })}
                placeholder="240 000 zł"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Kategoria</Label>
                <Select
                  value={taxDataForm.category}
                  onValueChange={(v) => setTaxDataForm({ ...taxDataForm, category: v })}
                >
                  <SelectTrigger data-testid="tax-data-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VAT">VAT</SelectItem>
                    <SelectItem value="PIT">PIT</SelectItem>
                    <SelectItem value="CIT">CIT</SelectItem>
                    <SelectItem value="CIT/PIT">CIT/PIT</SelectItem>
                    <SelectItem value="ZUS">ZUS</SelectItem>
                    <SelectItem value="KSeF">KSeF</SelectItem>
                    <SelectItem value="Rachunkowość">Rachunkowość</SelectItem>
                    <SelectItem value="Amortyzacja">Amortyzacja</SelectItem>
                    <SelectItem value="Ryczałt">Ryczałt</SelectItem>
                    <SelectItem value="Inne">Inne</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Rok</Label>
                <Input
                  data-testid="tax-data-year"
                  type="number"
                  value={taxDataForm.year}
                  onChange={(e) => setTaxDataForm({ ...taxDataForm, year: parseInt(e.target.value) || new Date().getFullYear() })}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Podstawa prawna</Label>
              <Input
                data-testid="tax-data-legal-basis"
                value={taxDataForm.legal_basis}
                onChange={(e) => setTaxDataForm({ ...taxDataForm, legal_basis: e.target.value })}
                placeholder="Art. 113 ust. 1 ustawy o VAT"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Uwagi (opcjonalne)</Label>
              <Input
                data-testid="tax-data-notes"
                value={taxDataForm.notes}
                onChange={(e) => setTaxDataForm({ ...taxDataForm, notes: e.target.value })}
                placeholder="Dodatkowe informacje..."
              />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              Te dane mają najwyższy priorytet w odpowiedziach KAI. AI zawsze poda te wartości zamiast starych danych z pamięci modelu.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaxDataDialog(false)}>
              Anuluj
            </Button>
            <Button data-testid="save-tax-data-btn" onClick={handleSaveTaxData} disabled={!taxDataForm.name || !taxDataForm.value}>
              {editingTaxData ? 'Zapisz zmiany' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Law Change Dialog */}
      <Dialog open={showLawChangeDialog} onOpenChange={setShowLawChangeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              {editingLawChange ? 'Edytuj zmianę w przepisach' : 'Dodaj zmianę w przepisach'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Tytuł *</Label>
              <Input value={lawChangeForm.title} onChange={(e) => setLawChangeForm(f => ({ ...f, title: e.target.value }))} placeholder="np. KSeF obowiązkowy od 1.02.2026" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Szczegółowy opis zmiany *</Label>
              <textarea
                className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md text-sm"
                value={lawChangeForm.description}
                onChange={(e) => setLawChangeForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Opisz dokładnie na czym polega zmiana, jakie przepisy się zmieniają..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Ważność</Label>
                <Select value={lawChangeForm.severity} onValueChange={(v) => setLawChangeForm(f => ({ ...f, severity: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Krytyczna</SelectItem>
                    <SelectItem value="important">Ważna</SelectItem>
                    <SelectItem value="info">Informacyjna</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Kategoria</Label>
                <Input value={lawChangeForm.category} onChange={(e) => setLawChangeForm(f => ({ ...f, category: e.target.value }))} placeholder="VAT, PIT, CIT, ZUS, KSeF..." />
              </div>
            </div>
            
            {/* Nowe pola - szczegóły zmiany */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Szczegóły praktyczne
              </h4>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block text-violet-700">👥 Kogo dotyczy?</Label>
                  <textarea
                    className="w-full min-h-[60px] px-3 py-2 border border-violet-200 rounded-md text-sm bg-violet-50/50"
                    value={lawChangeForm.who_affected}
                    onChange={(e) => setLawChangeForm(f => ({ ...f, who_affected: e.target.value }))}
                    placeholder="np. Podatnicy VAT czynni, firmy z obrotem powyżej 200 tys. zł, pracodawcy..."
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block text-green-700">✅ Co musisz zrobić?</Label>
                  <textarea
                    className="w-full min-h-[60px] px-3 py-2 border border-green-200 rounded-md text-sm bg-green-50/50"
                    value={lawChangeForm.action_required}
                    onChange={(e) => setLawChangeForm(f => ({ ...f, action_required: e.target.value }))}
                    placeholder="np. Zarejestrować się w systemie KSeF, zaktualizować oprogramowanie księgowe..."
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block text-red-700">⚠️ Konsekwencje nieprzestrzegania</Label>
                  <textarea
                    className="w-full min-h-[60px] px-3 py-2 border border-red-200 rounded-md text-sm bg-red-50/50"
                    value={lawChangeForm.consequences}
                    onChange={(e) => setLawChangeForm(f => ({ ...f, consequences: e.target.value }))}
                    placeholder="np. Kary finansowe, odmowa odliczenia VAT, sankcje karne skarbowe..."
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Data obowiązywania</Label>
                <Input type="date" value={lawChangeForm.effective_date} onChange={(e) => setLawChangeForm(f => ({ ...f, effective_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Termin wdrożenia</Label>
                <Input type="date" value={lawChangeForm.deadline} onChange={(e) => setLawChangeForm(f => ({ ...f, deadline: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Podstawa prawna</Label>
              <Input value={lawChangeForm.legal_basis} onChange={(e) => setLawChangeForm(f => ({ ...f, legal_basis: e.target.value }))} placeholder="Art. 113 ustawy o VAT, Dz.U. 2026 poz. 123" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLawChangeDialog(false)}>Anuluj</Button>
            <Button
              data-testid="save-law-change-btn"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  if (editingLawChange) {
                    await axios.put(`${API_URL}/admin/law-changes/${editingLawChange.id}`, lawChangeForm, {
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                    });
                    toast.success('Zmiana zaktualizowana');
                  } else {
                    await axios.post(`${API_URL}/admin/law-changes`, lawChangeForm, {
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                    });
                    toast.success('Zmiana dodana');
                  }
                  setShowLawChangeDialog(false);
                  setEditingLawChange(null);
                  loadAdminData();
                } catch { toast.error('Błąd zapisywania'); }
              }}
              disabled={!lawChangeForm.title || !lawChangeForm.description}
            >
              {editingLawChange ? 'Zapisz zmiany' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversation Detail Dialog */}
      <Dialog open={showConversationDialog} onOpenChange={setShowConversationDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {selectedConversation?.conversation?.title || 'Rozmowa'}
            </DialogTitle>
            <div className="text-xs text-slate-500">
              {selectedConversation?.user?.email} | {conversationMessages.length} wiadomości
            </div>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-3">
            <div className="space-y-4">
              {conversationMessages.map((msg, idx) => (
                <div key={msg.id || idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`conv-msg-${idx}`}>
                  <div className={`max-w-[85%] rounded-lg p-3 ${msg.role === 'user' ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50 border border-slate-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-semibold uppercase ${msg.role === 'user' ? 'text-blue-600' : 'text-primary-900'}`}>
                        {msg.role === 'user' ? 'Użytkownik' : 'KAI'}
                      </span>
                      <span className="text-[10px] text-slate-400">{msg.timestamp ? new Date(msg.timestamp).toLocaleString('pl-PL') : ''}</span>
                      {msg.verification && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          msg.verification.status === 'verified_multi' ? 'bg-green-100 text-green-700' :
                          msg.verification.status === 'verified_kb' ? 'bg-blue-100 text-blue-700' :
                          msg.verification.status === 'verified_web' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {msg.verification.status === 'verified_multi' ? 'Baza+Web' :
                           msg.verification.status === 'verified_kb' ? 'Baza' :
                           msg.verification.status === 'verified_web' ? 'Web' : 'Niezweryfikowane'}
                        </span>
                      )}
                      {msg.verification?.result && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${msg.verification.result.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {msg.verification.result.is_correct ? 'Poprawna' : 'Niepoprawna'}
                          {msg.verification.result.confidence ? ` (${Math.round(msg.verification.result.confidence * 100)}%)` : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{(msg.content || '').substring(0, 1500)}{(msg.content || '').length > 1500 ? '...' : ''}</div>
                    {msg.verification?.result?.issues?.length > 0 && (
                      <div className="mt-2 p-2 bg-red-50 rounded border border-red-100">
                        <div className="text-[10px] font-semibold text-red-700 mb-1">Problemy:</div>
                        <ul className="text-[10px] text-red-600 list-disc pl-4">
                          {msg.verification.result.issues.map((issue, j) => <li key={j}>{issue}</li>)}
                        </ul>
                      </div>
                    )}
                    {msg.citations?.length > 0 && (
                      <div className="mt-2 text-[10px] text-slate-400">
                        Źródła: {msg.citations.map(c => c.source || c.title || '').filter(Boolean).join(', ')}
                      </div>
                    )}
                    {msg.is_correction && (
                      <div className="mt-1 text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded inline-block">Korekta użytkownika</div>
                    )}
                  </div>
                </div>
              ))}
              {conversationMessages.length === 0 && (
                <div className="text-center py-8 text-slate-400">Brak wiadomości w tej rozmowie</div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
